/**
 * Length rule for a Listening (dictation) text (UD-545). Learners get 20 minutes to type
 * what they hear, so the text should stay inside this range. Both limits are inclusive.
 * The rule only warns: saving a text outside the range is always allowed.
 */
export const DICTATION_MIN_WORDS = 50
export const DICTATION_MAX_WORDS = 150

export type DictationLengthStatus = 'ok' | 'too_short' | 'too_long'

/** Words are whatever whitespace separates; an empty or blank text has 0 words. */
export const countWords = (text?: string | null): number => {
	const trimmed = String(text ?? '').trim()
	if (!trimmed) return 0
	return trimmed.split(/\s+/).length
}

export const classifyDictationLength = (wordCount: number): DictationLengthStatus => {
	if (wordCount < DICTATION_MIN_WORDS) return 'too_short'
	if (wordCount > DICTATION_MAX_WORDS) return 'too_long'
	return 'ok'
}

export const DICTATION_LENGTH_STATUS_LABEL: Record<Exclude<DictationLengthStatus, 'ok'>, string> = {
	too_short: 'Too short',
	too_long: 'Too long',
}

/**
 * The label of a Listening topic the server skips when it picks the topic of a day (UD-545),
 * for example "Skipped: too long (332 words)" or "Skipped: no audio". The reason is the
 * `skip_reason` the server sends with each topic of the list: it applies the rule (an audio,
 * a text, 50 to 150 words) itself and is the one that skips, so the page shows its word and
 * does not work the reason out again. A server from before UD-545 sends no reason, and then
 * there is no label.
 */
export const dictationSkippedLabel = (skipReason: unknown): string | null => {
	const reason = typeof skipReason === 'string' ? skipReason.trim() : ''
	return reason ? `Skipped: ${reason}` : null
}

export const formatWordCount = (wordCount: number) =>
	`${wordCount} ${wordCount === 1 ? 'word' : 'words'}`

/** The line under the text field, for example "42 words, allowed 50 to 150". */
export const dictationWordCountLabel = (wordCount: number) =>
	`${formatWordCount(wordCount)}, allowed ${DICTATION_MIN_WORDS} to ${DICTATION_MAX_WORDS}`

export type DictationLengthInput = {
	id: string
	day: number
	title?: string | null
	text?: string | null
}

export type DictationLengthIssue = {
	id: string
	day: number
	title: string
	wordCount: number
	status: Exclude<DictationLengthStatus, 'ok'>
}

/**
 * The topics whose text is outside the range, by day. The topic list carries no dates,
 * so the page cannot tell which days were already served and the day order is used.
 */
export const findDictationLengthIssues = (topics: DictationLengthInput[]): DictationLengthIssue[] => {
	const issues: DictationLengthIssue[] = []
	topics.forEach((topic) => {
		const wordCount = countWords(topic.text)
		const status = classifyDictationLength(wordCount)
		if (status === 'ok') return
		issues.push({
			id: topic.id,
			day: topic.day,
			title: String(topic.title || '').trim() || 'Untitled',
			wordCount,
			status,
		})
	})
	return issues.sort((a, b) => a.day - b.day)
}

export const summarizeDictationLengthIssues = (issues: DictationLengthIssue[]) => {
	const tooLong = issues.filter((issue) => issue.status === 'too_long').length
	return { total: issues.length, tooLong, tooShort: issues.length - tooLong }
}

/** For example "52 Listening topics need attention: 0 too long, 52 too short". */
export const dictationLengthHeading = (issues: DictationLengthIssue[]) => {
	const { total, tooLong, tooShort } = summarizeDictationLengthIssues(issues)
	const subject = total === 1 ? 'Listening topic needs' : 'Listening topics need'
	return `${total} ${subject} attention: ${tooLong} too long, ${tooShort} too short`
}
